import Model from "./Model";
import ContentModel from "./ContentModel";
import AssertionModel from "./AssertionModel";
import ExamModel from './ExamModel';
import MaterialModel from './MaterialModel';
import ExamModeModel from './ExamModeModel';
import ReplyModel from './ReplyModel';
import ExamApplicationModel from './ExamApplicationModel';

export default class QuestionModel extends Model {
  id: number;
  title: string;
  slug: string;
  averageScore: number;
  answer: any;
  source: any;
  mode: string;
  alternatives: number;
  interactiveData: any;
  group: string;
  examModeId: number;
  examApplicationId: number;
  examQuestionIds: number[];
  materialIds: number[];
  tags: string;
  answered: boolean;
  distinctUserReplies: number;

  static __typename: string = "Question";

  constructor(question: any, getters:any) {
    super(getters);
    this.id = question.id;
    this.title = question.title;
    this.slug = question.slug;
    this.averageScore = question.averageScore;
    this.answer = question.answer;
    this.source = question.source || {};
    this.mode = question.mode;
    this.alternatives = question.alternatives;
    this.interactiveData = question.interactiveData;
    this.group = question.group;
    this.examApplicationId = question.examApplicationId;
    this.examModeId = question.examModeByExamModeId || question.examModeId;
    this.examQuestionIds = question.examQuestionsByQuestionId || [];
    this.materialIds = question.materialsByQuestionId || [];
    this.tags = question.tags;
    this.answered = question.answered;
    this.distinctUserReplies = question.distinctUserReplies;
  }

  legacyContent() {
    return this.contents().map((contentComponent: ContentModel) => {
      var content, url, size, origin, description, kalturaId;
      let behavior = contentComponent.behavior();
      switch (behavior) {
        case "text":
          content = contentComponent.text().body;
          break;
        case "video":
          const video = contentComponent.video();
          const providerType = video.providerType;
          const isKalturaProvider = (provider) => provider === 'KALTURA';
          content = video.providerId;
          behavior = isKalturaProvider(providerType) ? 'kalturaVideo' : 'video';
          kalturaId = isKalturaProvider(providerType) ? content : null;
          break;
        case "image":
          let image = contentComponent.image();
          content = "";
          url = image.url;
          size = image.resizeFactor;
          origin = image.source ? image.source.origin : null;
          description = image.description;
          break;
      }
      return {
        behavior,
        content,
        url,
        size,
        kalturaId,
        origin,
        description
      };
    });
  }

  contentString(): string {
    return this.legacyContent()
      .filter((contentComponent: any) => contentComponent.behavior === "text")
      .reduce((acc: string, cur: any) => (acc += cur.content), "");
  }

  contents(): ContentModel[] {
    let contents = this.getters
      .contentsByQuestionId(this.id)
      .concat()
      .sort((A: ContentModel, B: ContentModel) => A.position - B.position);
    return contents;
  }

  examApplications(): ExamApplicationModel[] {
    return this.getters.questionApplicationsByQuestionId(this.id)
      .map((questionApplication) => questionApplication.examApplication())
  }

  legacyInteractiveData() {
    return {
      alternatives: this.assertions()
        .map((assertion:AssertionModel) => assertion.legacyContent())
    }
  }

  assertions(): AssertionModel[] {
    return this.getters
      .assertionsByQuestionId(this.id)
      .concat()
      .sort((A: AssertionModel, B: AssertionModel) => A.position - B.position);
  }

  computedAnswer() {
    const correctAssertion = this.assertions()
      .find((assertion:AssertionModel) => assertion.correct);
    if (correctAssertion) {
      return String(correctAssertion.position);
    } else {
      return this.answer;
    }
  }

  exams(): ExamModel[] {
    return this.examQuestionIds.map((examQuestionId: number) =>
      this.getters.examQuestionById(examQuestionId).exam()
    );
  }

  materials(): MaterialModel[] {
    return this.materialIds.map((materialId: number) =>
      this.getters.materialById(materialId)
    );
  }

  examMode(): ExamModeModel {
    return this.getters.examModeById(this.examModeId);
  }

  replies(): ReplyModel[] {
    return this.getters.repliesByQuestionId(this.id);
  }

  raw() {
    return {
      id: this.id,
      title: this.title,
      content: this.legacyContent(),
      examModeId: this.examModeId,
      answer: this.computedAnswer(),
      source: this.source,
      mode: this.mode,
      alternatives: this.alternatives,
      interactiveData: this.legacyInteractiveData(),
      group: this.group,
      tags: this.tags,
      examApplicationId: this.examApplicationId,
      __typename: QuestionModel.__typename
    };
  }
}